import React, { useEffect, useRef, useState } from "react";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import PrimeReact from "primereact/api";

import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "primereact/tooltip";

import { useHistory, useLocation } from "react-router-dom";
import { useToast } from "../../../hooks/toast";
import { Container } from "./styles";
import api from "../../../services/api";

interface ILawsuitProperties {
  proc_id: number;
  proc_reg: string;
  proc_num_pasta: string;
  proc_reg_outros: string;
  proc_autor: string;
  proc_reu: string;
  acao_descricao: string;
  proc_assunto: string;
  proc_ult_atu: Date | string | null;
  cliente_nome: string;
  proc_cod_cliente: number;
}

const SelectFinancialInformationModIII: React.FC = () => {
  PrimeReact.ripple = true;

  const [lawsuits, setLawsuits] = useState<ILawsuitProperties[]>([]);
  const [selectedLawsuits, setSelectedLawsuits] = useState<
    ILawsuitProperties[]
  >([]);
  const [loading, setLoading] = useState(true);

  // para tabela
  const [globalFilter, setGlobalFilter] = useState("");
  const dt = useRef(null);

  const { addToast } = useToast();
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const state = location.state as any;

        const response = await api.post(
          "/reports/financialinformation/modiii/lawsuits",
          state
        );

        setLawsuits(response.data);
      } catch (err) {
        addToast({
          type: "error",
          title: "Ocorreu um problema",
          description:
            "Não foi possível carregar os processos. Tente repetir a pesquisa ou contate o administrador.",
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [addToast, location]);

  const assuntoUltimaAtualizacaoBodyTemplate = (rowData: {
    proc_assunto: string;
    proc_ult_atu: Date | string | null;
  }) => {
    let formattedDate = "-";

    if (rowData.proc_ult_atu) {
      const date = new Date(rowData.proc_ult_atu);
      formattedDate = date.toLocaleDateString("pt-BR", { timeZone: "UTC" });
    }

    const assunto = rowData.proc_assunto ? rowData.proc_assunto : "-";

    return (
      <i
        className="pi pi-info-circle info-icon"
        data-pr-tooltip={`Assunto: ${assunto}\nAtualizado até: ${formattedDate}`}
      />
    );
  };

  const handleNewSearch = () => {
    history.push("/report/financialinformationmodIIIselect/search");
  };

  const handleGenerateReport = () => {
    if (selectedLawsuits.length === 0) {
      addToast({
        type: "info",
        title: "Nenhum processo selecionado",
        description: "Selecione ao menos um processo para gerar o relatório.",
      });
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const state = location.state as any;

    const reportFormData = {
      ...state,
      proc_ids: selectedLawsuits.map((lawsuit) => lawsuit.proc_id),
    };

    history.push({
      pathname: "/report/financialinformationmodIII",
      state: reportFormData,
    });
  };

  const header = (
    <div className="flex align-items-center export-buttons">
      <Button
        icon="pi pi-search"
        iconPos="left"
        label="Nova consulta"
        className="p-button p-component p-button-secondary p-mr-2"
        type="button"
        onClick={handleNewSearch}
      />
      <Button
        icon="pi pi-file-pdf"
        iconPos="left"
        label="Gerar relatório"
        className="p-button p-component p-button-primary p-mr-2"
        type="button"
        onClick={handleGenerateReport}
        disabled={loading}
      />
      <span className="p-input-icon-left p-ml-auto">
        <i className="pi pi-search" />
        <InputText
          type="search"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Pesquisar na tabela"
        />
      </span>
    </div>
  );

  const paginatorLeft = (
    <Button type="button" icon="pi pi-refresh" className="p-button-text" />
  );
  const paginatorRight = (
    <Button type="button" icon="pi pi-cloud" className="p-button-text" />
  );

  return (
    <Container>
      <header>
        <h3>
          Relatório de Informações Processuais - Modelo III{" "}
          <small>(Seleção de processos)</small>
        </h3>
      </header>

      <div className="datatable-responsive">
        <div className="card">
          <Tooltip target=".export-buttons>button" position="bottom" />
          <Tooltip
            target=".info-icon"
            mouseTrack
            mouseTrackLeft={10}
            position="top"
          />

          <DataTable
            tableClassName="table-lawsuits table"
            id="table-lawsuits"
            ref={dt}
            value={lawsuits}
            loading={loading}
            dataKey="proc_id"
            header={header}
            stripedRows
            className="p-datatable-sm"
            paginator
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Exibindo {first} a {last} de {totalRecords}"
            rows={50}
            rowsPerPageOptions={[10, 20, 50]}
            paginatorLeft={paginatorLeft}
            paginatorRight={paginatorRight}
            globalFilter={globalFilter}
            emptyMessage="Nenhum registro encontrado."
            selectionMode="checkbox"
            selection={selectedLawsuits}
            loadingIcon="pi pi-spin pi-spinner"
            onSelectionChange={(e) => {
              const selLawsuits = e.value as ILawsuitProperties[];
              setSelectedLawsuits(selLawsuits);
            }}
          >
            <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
            <Column field="proc_reg" header="Processo nº" sortable />
            <Column field="proc_num_pasta" header="Pasta" sortable />
            <Column
              field="proc_reg_outros"
              header="Outros nrs. do processo"
              sortable
            />
            <Column field="proc_autor" header="Autor(es)" sortable />
            <Column field="proc_reu" header="Réu(s)" sortable />
            <Column field="acao_descricao" header="Tipo de ação" sortable />
            <Column
              header=""
              alignHeader="center"
              align="center"
              body={assuntoUltimaAtualizacaoBodyTemplate}
              style={{ width: "50px" }}
            />
          </DataTable>
        </div>
      </div>
    </Container>
  );
};

export default SelectFinancialInformationModIII;
